import React, { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Bell, Loader2, Heart, MessageCircle, UserPlus, CheckCheck, Gift, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const notificationIcons = {
  like: Heart,
  comment: MessageCircle,
  follow: UserPlus,
  message: MessageCircle,
  shop_gift: Gift,
  gift: Gift,
  live: Radio,
  cohost_invite: UserPlus,
  coins: Gift,
};

export default function NotificationsPage() {
  const { token, language } = useAuth();
  const tr = t(language);
  const { fetchUnreadCount } = useOutletContext();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      fetchUnreadCount?.();
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  };

  const getNotificationLink = (notification) => {
    switch (notification.type) {
      case 'like':
      case 'comment':
        return `/post/${notification.reference_id}`;
      case 'follow':
        return `/profile/${notification.actor_username}`;
      case 'message':
        return `/chat/${notification.reference_id}`;
      case 'shop_gift':
      case 'gift':
        return `/profile/me`;
      case 'live':
      case 'cohost_invite':
        return `/live/${notification.reference_id}`;
      default:
        return '#';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="max-w-2xl mx-auto" data-testid="notifications-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">{tr('notifications')}</h1>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllRead}
              className="text-primary"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              {tr('markAllRead')}
            </Button>
          )}
        </div>
      </header>

      {/* Notifications List */}
      <div className="divide-y divide-border/40">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <Bell className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-center">{tr('noNotifications')}</p>
          </div>
        ) : (
          notifications.map(notification => {
            const Icon = notificationIcons[notification.type] || Bell;
            
            return (
              <Link
                key={notification.id}
                to={getNotificationLink(notification)}
                className={`flex items-start gap-3 p-4 hover:bg-secondary/30 transition-colors ${
                  !notification.is_read ? 'bg-primary/5' : ''
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <div className="relative">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={notification.actor_avatar} alt={notification.actor_username} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(notification.actor_username)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                    <Icon className="w-3 h-3" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-semibold">@{notification.actor_username}</span>
                    {' '}
                    {notification.message.replace(`@${notification.actor_username}`, '').trim()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTimeAgo(notification.created_at, language)}
                  </p>
                </div>
                
                {!notification.is_read && (
                  <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                )}
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
